<template>
    <div class="engineer-overtime">
        <div class="engineer-overtime__header">
            <h1
                class="engineer-overtime__title"
                v-if="isNew"
            >
                New Overtime
            </h1>
            <h1
                class="engineer-overtime__title"
                v-if="!isNew"
            >
                Edit Overtime
            </h1>
        </div>
        <div
            class="engineer-overtime__form"
            v-if="loading"
        >
            <p>Loading...</p>
        </div>
        <form
            action=""
            @submit.prevent="saveOvertime"
            class="engineer-overtime__form"
            v-if="!loading"
        >
            <div class="row">
                <div class="col-sm-12 col-md-6">
                    <div class="form-group">
                        <label for="start_time">Start Time:</label>
                        <date-picker
                            v-model="overtime.start_time"
                            id="start_time"
                            :readonly="!canEdit"
                        />
                        <error
                            :errors="errors"
                            name="start_time"
                        />
                    </div>
                </div>
                <div class="col-sm-12 col-md-6">
                    <div class="form-group">
                        <label for="end_time">End Time:</label>
                        <date-picker
                            v-model="overtime.end_time"
                            id="end_time"
                            :readonly="!canEdit"
                        />
                        <error
                            :errors="errors"
                            name="end_time"
                        />
                    </div>
                </div>
            </div>
            <div class="row">
                <div class="col-sm-12">
                    <vehicle-select
                        v-model="overtime.vehicle_reg"
                        label="Vehicle Reg (optional):"
                        optional
                        optional-value=""
                        :readonly="!canEdit"
                        :errors="errors"
                    />
                </div>
            </div>
            <div class="row">
                <div class="col-sm-12">
                    <label for="location">Location:</label>
                    <input
                        type="text"
                        class="form-control"
                        placeholder="Location"
                        v-model="overtime.location"
                        :readonly="!canEdit"
                    >
                    <error
                        :errors="errors"
                        name="location"
                    />
                </div>
            </div>
            <div class="row">
                <div class="col-sm-12">
                    <div class="form-group">
                        <label for="out_of_hours">Out-of-hours Emergency Call-out?</label>
                        <select
                            id="out_of_hours"
                            v-model="overtime.out_of_hours"
                            class="form-control"
                            :readonly="!canEdit"
                        >
                            <option :value="false">
                                No
                            </option>
                            <option :value="true">
                                Yes
                            </option>
                        </select>
                    </div>
                </div>
            </div>
            <div class="row">
                <div class="col-sm-12">
                    <div class="form-group">
                        <label for="out_of_hours">Includes overnight stay?</label>
                        <select
                            id="out_of_hours"
                            v-model="overtime.overnight_stay"
                            class="form-control"
                            :readonly="!canEdit"
                        >
                            <option :value="false">
                                No
                            </option>
                            <option :value="true">
                                Yes
                            </option>
                        </select>
                    </div>
                </div>
            </div>
            <div class="row">
                <div class="col-sm-12">
                    <p>
                        <span v-if="overtime.verified_by"><i class="fa fa-check" /> Verified by {{ overtime.verified_by.name }} </span>
                    </p>
                </div>
            </div>
            <div class="row engineer-overtime__form-footer">
                <div class="col-sm-12">
                    <router-link
                        :to="{name: 'engineer.overtime.index'}"
                        class="btn btn-default"
                    >
                        Back
                    </router-link>
                    <button
                        class="btn btn-danger"
                        v-if="!isNew"
                        @click.prevent="deleteOvertime"
                        :disabled="!canEdit"
                    >
                        Delete
                    </button>
                    <button
                        class="btn btn-primary"
                        :disabled="saving || !canEdit"
                    >
                        <span v-if="!saving">Save</span>
                        <span v-else>Saving...</span>
                    </button>
                </div>
            </div>
        </form>
    </div>
</template>

<script>
import OvertimeService from "../../services/OvertimeService";
import DatePicker from "../DateTimePickerComponent";
import VehicleSelect from "../VehicleSelectComponent.vue";
import UsesErrors from "../../mixins/UsesErrors";
import UsesInfoMessage from "../../mixins/UsesInfoMessage";

export default {
    components: {
        DatePicker,
        VehicleSelect,
    },
    mixins: [UsesErrors, UsesInfoMessage],
    props: {
        id: {
            required: false,
            default: null,
            type: [String, Number],
        },
    },
    data() {
        return {
            overtime: {
                start_time: window.moment().startOf("hour"),
                end_time: window.moment().startOf("hour").add(1, "hour"),
                out_of_hours: false,
                overnight_stay: false,
                vehicle_reg: '',
            },
            saving: false,
            loading: false,
        };
    },
    computed: {
        isNew() {
            return this.overtime.id === undefined;
        },
        paidAt() {
            if (!this.overtime.paid_at) {
                return null;
            }

            return window
                .moment(this.overtime.paid_at)
                .format("DD/MM/YYYY HH:mm");
        },
        canEdit() {
            return !this.overtime.verified_by;
        },
    },
    watch: {
        id: {
            handler: function () {
                if (this.id) {
                    this.loadOvertime();
                    return;
                }
                this.resetOvertime();
            },
            immediate: true,
        },
    },
    methods: {
        saveOvertime() {
            this.saving = true;
            OvertimeService.save(this.overtime)
                .then((res) => {
                    this.setInfoMessage("Saved successfully");
                    if (this.isNew) {
                        this.$router.push({
                            name: "engineer.overtime.edit",
                            params: { id: res.data.id },
                        });
                        return;
                    }
                    this.overtime = res.data;
                })
                .catch((err) => {
                    this.captureRequestErrors(err);
                })
                .then(() => {
                    this.saving = false;
                });
        },
        loadOvertime() {
            this.loading = true;
            OvertimeService.get(this.id)
                .then((res) => {
                    this.setOvertime(res.data);
                    this.loading = false;
                })
                .catch((err) => {
                    window.alert("Error: Failed to load overtime log");
                    this.$router.replace({ name: "engineer.overtime.index" });
                    throw err;
                });
        },
        setOvertime(data) {
            let overtime = { ...data };
            overtime.start_time = window.moment(data.start_time);
            overtime.end_time = window.moment(data.end_time);
            overtime.out_of_hours = data.out_of_hours === 1;
            overtime.overnight_stay = data.overnight_stay === 1;
            overtime.vehicle_reg = data.vehicle_reg ? data.vehicle_reg.trim() : '';
            this.overtime = overtime;
        },
        resetOvertime() {
            this.overtime = {
                out_of_hours: false,
                overnight_stay: false,
                start_time: window.moment().startOf("hour"),
                end_time: window.moment().startOf("hour").add(1, "hour"),
                location: '',
                vehicle_reg: '',
            };
        },
        deleteOvertime() {
            if (
                !window.confirm(
                    "Are you sure want to delete this overtime log?"
                )
            ) {
                return;
            }
            this.loading = true;
            OvertimeService.delete(this.overtime.id).then(() => {
                this.setInfoMessage("Deleted successfully");
                this.$router.replace({ name: "engineer.overtime.index" });
            });
        },
    },
};
</script>


// WEBPACK FOOTER //
// resources/assets/js/components/engineer/OvertimeForm.vue